import { Controller, useForm } from 'react-hook-form';
import { Button } from '../ui/button';
import { InputLabel } from '../ui/InputLabel';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { createWorkspace, updateAuthUser } from '@/features/auth/authThunks';
import type { RootState } from '@/app/store';
import { Link, useNavigate } from 'react-router-dom';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { PhoneSelectInput } from '../ui/PhoneSelectInput';
import Loader from '../ui/Loader';
import { toast } from 'react-toastify';
import type { AppError } from '@/features/auth/types';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select';
import { getWorkspaceLanguage } from '@/i18n/workspaceLanguage';
import { useSubmitLock } from '@/hooks/useSubmitLock';
import {
  LIBERTY_WEBI_PROMO_CODE,
  clearStoredAffiliate,
  isLibertyWebiReferral,
  readStoredAffiliate,
} from '@/lib/affiliateReferral';

type FormValues = {
  name: string;
  firstname: string;
  phone: string;
  company: string;
  promoCode: string;
  timezone: string;
};

export default function NewUserInformationForm() {
  const { t, i18n } = useTranslation(['new-user', 'auth', 'common']);
  const { t: tUser } = useTranslation('user');
  const dispatch = useAppDispatch();
  const { error } = useAppSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { isLocked, runExclusive } = useSubmitLock();
  const [storedAffiliate] = useState<string | null>(() => readStoredAffiliate(Date.now()));
  // Offre partenaire : le code est imposé par le lien d'affiliation, on le verrouille.
  const isPartnerPromoCodeLocked = isLibertyWebiReferral(storedAffiliate);
  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .refine((val) => val.trim().length > 0, {
            message: tUser('validation.lastName.required'),
          })
          .refine((val) => val.length <= 24, { message: tUser('validation.lastName.max') }),
        firstname: z
          .string()
          .refine((val) => val.trim().length > 0, {
            message: tUser('validation.firstName.required'),
          })
          .refine((val) => val.length <= 24, { message: tUser('validation.firstName.max') }),
        phone: z
          .string()
          .min(1, { message: tUser('validation.phoneNumber.required') })
          .refine(
            (val) => {
              if (!val || val.trim() === '') return true;
              return isValidPhoneNumber(val);
            },
            { message: tUser('validation.phoneNumber.invalid') },
          )
          .refine(
            (val) => {
              if (!val || val.trim() === '') return true;
              return val.length <= 20;
            },
            { message: tUser('validation.phoneNumber.maxLength') },
          ),
        company: z
          .string()
          .refine((val) => val.trim().length > 0, {
            message: tUser('validation.company.required'),
          })
          .refine((val) => val.length <= 24, { message: tUser('validation.company.max') }),
        promoCode: z.string().refine((val) => val.length <= 50, {
          message: tUser('validation.promoCode.max'),
        }),
        timezone: z.string().refine((val) => Intl.supportedValuesOf('timeZone').includes(val), {
          message: tUser('validation.timezone.invalid'),
        }),
      }),
    [t, tUser],
  );
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      firstname: '',
      phone: '',
      company: '',
      promoCode: isLibertyWebiReferral(storedAffiliate) ? LIBERTY_WEBI_PROMO_CODE : '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Europe/Paris',
    },
    mode: 'onChange',
  });

  const submitInformation = async (data: FormValues) => {
    try {
      setLoading(true);
      const updateResult = await dispatch(
        updateAuthUser({
          lastName: data.name.trim(),
          firstName: data.firstname.trim(),
          phoneNumber: data.phone.trim(),
          companyName: data.company.trim(),
        }),
      );

      if (updateAuthUser.fulfilled.match(updateResult)) {
        // Le back n'accorde l'offre partenaire que sur le code promo : un champ vidé ne doit pas la faire perdre.
        const promoCode =
          data.promoCode?.trim() ||
          (isLibertyWebiReferral(storedAffiliate) ? LIBERTY_WEBI_PROMO_CODE : '');
        const referralCode = storedAffiliate?.trim();
        const workspaceResult = await dispatch(
          createWorkspace({
            name: data.company.trim(),
            timezone: data.timezone.trim(),
            language: getWorkspaceLanguage(i18n.language),
            ...(promoCode && { promoCode }),
            ...(referralCode && { referralCode }),
          }),
        );

        if (createWorkspace.fulfilled.match(workspaceResult)) {
          clearStoredAffiliate();
          navigate('/', { replace: true });
        } else if (createWorkspace.rejected.match(workspaceResult)) {
          const error = workspaceResult.payload as AppError | undefined;
          const errorMessage = error?.message ? t(error.message) : t('errors.generic');
          toast.error(errorMessage);
        }
      } else if (updateAuthUser.rejected.match(updateResult)) {
        const error = updateResult.payload as AppError | undefined;
        const errorMessage = error?.message ? t(error.message) : t('errors.generic');
        toast.error(errorMessage);
      }
    } catch (err) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data: FormValues) => runExclusive(() => submitInformation(data));

  const isValid = form.formState.isValid;
  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="space-y-1 w-full">
          <Controller
            name="firstname"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('firstname')}
                id="firstname"
                type="text"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={(e) => {
                  const newValue = e.target.value.slice(0, 25);
                  field.onChange(newValue);
                }}
                maxLength={25}
              />
            )}
          />
          {form.formState.errors.firstname && (
            <p className="text-xs text-error-400">{form.formState.errors.firstname.message}</p>
          )}
        </div>

        <div className="space-y-1 w-full">
          <Controller
            name="name"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('name')}
                id="name"
                type="text"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={(e) => {
                  const newValue = e.target.value.slice(0, 25);
                  field.onChange(newValue);
                }}
                maxLength={25}
              />
            )}
          />
          {form.formState.errors.name && (
            <p className="text-xs text-error-400">{form.formState.errors.name.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <Controller
          name="phone"
          control={form.control}
          render={({ field }) => (
            <PhoneSelectInput
              defaultCountry="FR"
              value={field.value || undefined}
              onChange={(value) => {
                field.onChange(value || '');
              }}
              label={`${t('phone')}`}
              className="w-full border-input-inverse bg-transparent h-14"
              classNameLabel="text-tertiary-inverse"
            />
          )}
        />
        {form.formState.errors.phone && (
          <p className="text-xs text-error-400">{form.formState.errors.phone.message}</p>
        )}
      </div>

      <div className="space-y-1">
        <Controller
          name="company"
          control={form.control}
          render={({ field }) => (
            <InputLabel
              label={`${t('company')}*`}
              id="company"
              type="text"
              className="border-input-inverse bg-transparent"
              classNameLabel="text-tertiary-inverse"
              classNameInput="text-white"
              value={field.value}
              onChange={(e) => {
                const newValue = e.target.value.slice(0, 25);
                field.onChange(newValue);
              }}
              maxLength={25}
            />
          )}
        />
        {form.formState.errors.company && (
          <p className="text-xs text-error-400">{form.formState.errors.company.message}</p>
        )}
      </div>
      <div className="space-y-1">
        <Controller
          name="timezone"
          control={form.control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger
                label={t('timezone')}
                className="border-input-inverse bg-transparent"
              />
              <SelectContent>
                {Intl.supportedValuesOf('timeZone').map((timezone) => (
                  <SelectItem key={timezone} value={timezone}>
                    {timezone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {form.formState.errors.timezone && (
          <p className="text-xs text-error-400">{form.formState.errors.timezone.message}</p>
        )}
      </div>
      <div className="space-y-1">
        <Controller
          name="promoCode"
          control={form.control}
          render={({ field }) => (
            <InputLabel
              label={t('codePromo')}
              id="promoCode"
              type="text"
              disabled={isPartnerPromoCodeLocked}
              className="border-input-inverse bg-transparent"
              classNameLabel="text-tertiary-inverse"
              classNameInput={isPartnerPromoCodeLocked ? 'text-white/60' : 'text-white'}
              value={field.value}
              onChange={(e) => {
                const newValue = e.target.value.slice(0, 50);
                field.onChange(newValue);
              }}
              maxLength={50}
            />
          )}
        />
        {isPartnerPromoCodeLocked && (
          <p className="text-xs text-tertiary-inverse">{t('codePromoPartenaire')}</p>
        )}
        {form.formState.errors.promoCode && (
          <p className="text-xs text-error-400">{form.formState.errors.promoCode.message}</p>
        )}
      </div>

      {error && <p className="text-xs text-error-400 mt-2">{t(error) || error}</p>}
      <Button
        type="submit"
        className="w-full h-11 rounded-full mt-6 text-base disabled:bg-button-primary-disable-inverse disabled:text-disable"
        disabled={!isValid || loading || isLocked}
      >
        {loading || isLocked ? (
          <span className="flex items-center justify-center gap-2">
            <Loader />
            {t('loading')}
          </span>
        ) : (
          t('submit')
        )}
      </Button>
      <p className="mt-16 text-sm text-secondary-inverse">
        {t('termsPrefix')}{' '}
        <Link
          to="https://www.welya.io/cgu"
          target="_blank"
          className="text-highlighted-reverse cursor-pointer"
        >
          {t('termsLink')}
        </Link>{' '}
        {t('termsSuffix')}
      </p>
    </form>
  );
}
